import { useState } from 'react'
import { useTicketsStore, ensureUsername } from '@/stores/tickets'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Plus, Loader2 } from 'lucide-react'

export function TicketForm() {
  const { createTicket, isLoading } = useTicketsStore()
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    priority: 'medium' as const,
    category: 'general'
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Ensure username is set
    const username = ensureUsername()
    if (!username) {
      alert('Ein Name ist erforderlich um ein Ticket zu erstellen.')
      return
    }

    if (!formData.name.trim() || !formData.description.trim()) {
      alert('Bitte füllen Sie alle Pflichtfelder aus.')
      return
    }

    const ticket = await createTicket({
      name: formData.name.trim(),
      description: formData.description.trim(),
      priority: formData.priority,
      category: formData.category,
      ticket_type: 'emaildrafter'
    })

    if (ticket) {
      // Reset form
      setFormData({
        name: '',
        description: '',
        priority: 'medium',
        category: 'general'
      })
      alert('Ticket erfolgreich erstellt!')
    }
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus className="h-5 w-5" />
          Neues Ticket erstellen
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Titel *</Label>
              <Input
                id="name"
                placeholder="Kurze Beschreibung des Problems"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priorität</Label>
              <Select
                value={formData.priority}
                onValueChange={(value: any) => setFormData(prev => ({ ...prev, priority: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">
                    <span className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      Niedrig
                    </span>
                  </SelectItem>
                  <SelectItem value="medium">
                    <span className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                      Mittel
                    </span>
                  </SelectItem>
                  <SelectItem value="high">
                    <span className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                      Hoch
                    </span>
                  </SelectItem>
                  <SelectItem value="urgent">
                    <span className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      Dringend
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Kategorie</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">Allgemein</SelectItem>
                <SelectItem value="email">E-Mail Problem</SelectItem>
                <SelectItem value="system">System Fehler</SelectItem>
                <SelectItem value="feature">Feature Request</SelectItem>
                <SelectItem value="question">Frage</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Beschreibung *</Label>
            <Textarea
              id="description"
              placeholder="Detaillierte Beschreibung des Problems oder der Anfrage..."
              className="min-h-[120px]"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              required
            />
          </div>

          <div className="pt-4">
            <Button
              type="submit"
              disabled={isLoading || !formData.name.trim() || !formData.description.trim()}
              className="w-full sm:w-auto"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird erstellt...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Ticket erstellen
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}